
/*
 * ----------------------------------------------------------------------------------
 * Imports - External
 * ----------------------------------------------------------------------------------
 */
import { AlertTitle, Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Tooltip, styled } from '@mui/material';

import {
    IPatient,
    IPatientSummaryActionProps,
    useSnackbar
} from '@ngt/opms';
import { FC, useCallback, useState } from 'react';

/*
 * ----------------------------------------------------------------------------------
 * Imports - Internal
 * ----------------------------------------------------------------------------------
 */
import { JsonServiceClient } from '@servicestack/client';
import { PatientStateType, SendPatientReminder } from '../../../api/dtos';


const StyledTooltip = styled(Tooltip)(() => ({
    display: 'flex'
}))

const StyledTooltipWrapper = styled(Box)(() => ({
    display: 'flex'
}))

const StyledEventButton = styled(Button)(({ theme }) => ({
    minWidth: 140,
    borderRadius: 0,
    whiteSpace: 'nowrap',
    '&:first-of-type': {
        marginLeft: theme.spacing(0)
    }
}))



export enum PatientSummaryDialogState {
    None = 0,
    Create = 1,
    CreateAndNotify = 2,
    Resend = 3
}

const usePatientSummaryCreateAndNotifyActions = (
    updateTo: PatientStateType,
    hideButton: (patient: IPatient | null | undefined) => boolean,
    eventTitle: string,
    eventName: string,
    saving: boolean,
    updatePatientState: (state: PatientStateType, skipPatientEmail: boolean, patientStatusReason?: string) => void,
    enqueueSnackbar: ReturnType<typeof useSnackbar>['enqueueSnackbar'],
    client: JsonServiceClient,
    patient: IPatient | null | undefined,
    patientValid: boolean,
    disabledNotification?: boolean
) => {
    const [dialogState, setDialogState] = useState(PatientSummaryDialogState.None);

    const openCreateDialog = useCallback(() => {
        setDialogState(PatientSummaryDialogState.Create);
    }, [setDialogState]);

    const openCreateAndNotifyDialog = useCallback(() => {
        setDialogState(PatientSummaryDialogState.CreateAndNotify);
    }, [setDialogState]);

    const openResendDialog = useCallback(() => {
        setDialogState(PatientSummaryDialogState.Resend);
    }, [setDialogState]);

    const closeDialog = useCallback(() => {
        setDialogState(PatientSummaryDialogState.None);
    }, [setDialogState]);

    const onCreateAndNotifyClick = useCallback(async () => {
        if (patient) {

            let success = false;

            try {
                await client.post(new SendPatientReminder({
                    patientId: patient.id
                }));

                success = true;
            }
            catch {
                enqueueSnackbar(
                    <>
                        <AlertTitle>
                            Patient Notify Email Not Sent
                        </AlertTitle>
                        An error occurred while attempting to send the patient's notification email.
                    </>,
                    { variant: 'critical' }
                );
            }

            if (!success) {
                return;
            }
            
            try {
                
                await updatePatientState(updateTo, false);

                enqueueSnackbar(
                    <>
                        <AlertTitle>
                            {eventTitle} Event Created
                        </AlertTitle>
                        The {eventName} event was successfully created.
                    </>,
                    { variant: 'success' }
                );

                closeDialog();
            }
            catch {
                enqueueSnackbar(
                    <>
                        <AlertTitle>
                            {eventTitle} Event Not Created
                        </AlertTitle>
                        An error occurred while attempting to create the {eventName} event.
                    </>,
                    { variant: 'critical' }
                );

                closeDialog();
            }
        }
    }, [updatePatientState, enqueueSnackbar, updateTo, eventTitle, eventName, closeDialog, client, patient]);

    const onCreateClick = useCallback(async () => {
        try {
            await updatePatientState(updateTo, true);

            enqueueSnackbar(
                <>
                    <AlertTitle>
                        {eventTitle} Event Created
                    </AlertTitle>
                    The {eventName} event was successfully created.
                </>,
                { variant: 'success' }
            );

            closeDialog();
        }
        catch {
            enqueueSnackbar(
                <>
                    <AlertTitle>
                        {eventTitle} Event Not Created
                    </AlertTitle>
                    An error occurred while attempting to create the {eventName} event.
                </>,
                { variant: 'critical' }
            );

            closeDialog();
        }
    }, [updatePatientState, enqueueSnackbar, updateTo, eventTitle, eventName, closeDialog]);

    const [sendingEmail, setSendingEmail] = useState(false);

    const onResendClick = useCallback(async () => {
        setSendingEmail(true);

        try {
            await client.post(new SendPatientReminder({ patientId: patient?.id }));

            enqueueSnackbar(
                <>
                    <AlertTitle>
                        {eventTitle} Notification Sent
                    </AlertTitle>
                    The {eventName} notification was successfully sent to the patient.
                </>,
                { variant: 'success' }
            );

            closeDialog();
        }
        catch {
            enqueueSnackbar(
                <>
                    <AlertTitle>
                        {eventTitle} Notification Not Sent
                    </AlertTitle>
                    An error occurred while attempting to create the {eventName} notification to the patient.
                </>,
                { variant: 'critical' }
            );

            closeDialog();
        }

        setSendingEmail(false);
    }, [patient, enqueueSnackbar, client, setSendingEmail, eventName, eventTitle, closeDialog]);

    const action: FC<IPatientSummaryActionProps> = useCallback(({
        patient: actionPatient
    }) => {
        if (hideButton(actionPatient)) {
            return null;
        }

        if (actionPatient.patientStateId === updateTo) {
            return disabledNotification ?
                undefined
                :
                (
                    <>
                        <StyledEventButton
                            loading={saving || sendingEmail}
                            variant="contained"
                            color="primary"
                            onClick={openResendDialog}
                        >
                            Resend
                        </StyledEventButton>
                        <Dialog
                            open={dialogState === PatientSummaryDialogState.Resend}
                            onClose={closeDialog}
                            aria-labelledby="resend-dialog-title"
                            aria-describedby="resend-dialog-description"
                        >
                            <DialogTitle id="resend-dialog-title">Resend {eventTitle} Notification?</DialogTitle>
                            <DialogContent>
                            <DialogContentText id="resend-dialog-description">
                                    Resending the {eventName} notification will email the patient.
                                    <br /><br />
                                    Are you sure you wish to continue?
                            </DialogContentText>
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={closeDialog} color="secondary">
                                    No
                            </Button>
                                <Button onClick={onResendClick} color="primary" variant="contained" autoFocus>
                                    Yes
                            </Button>
                            </DialogActions>
                        </Dialog>
                    </>
                );
        }

        const createButton = (
            <StyledEventButton
                loading={saving}
                variant="contained"
                color="primary"
                onClick={openCreateDialog}
                disabled={!patientValid || saving}
            >
                Create
            </StyledEventButton>
        );

        const createAndNotifyButton = disabledNotification ?
            undefined
            :
            (
                <StyledEventButton
                    loading={saving}
                    variant="contained"
                    color="primary"
                    onClick={openCreateAndNotifyDialog}
                    disabled={!patientValid || saving}
                >
                    Create & Notify
                </StyledEventButton>
            );

        if (patientValid) {
            return <>
                {createButton}
                {createAndNotifyButton}
                <Dialog
                    open={dialogState === PatientSummaryDialogState.Create || dialogState === PatientSummaryDialogState.CreateAndNotify}
                    onClose={closeDialog}
                    aria-labelledby="create-dialog-title"
                    aria-describedby="create-dialog-description"
                >
                    <DialogTitle id="create-dialog-title">Create {eventTitle} Event?</DialogTitle>
                    <DialogContent>
                        {
                            dialogState === PatientSummaryDialogState.CreateAndNotify ?
                                <DialogContentText id="create-dialog-description">
                                    Creating the {eventName} Event will send an email notification to the patient.
                                    <br /><br />
                                    Are you sure you wish to continue?
                                </DialogContentText> :
                                <DialogContentText id="create-dialog-description">
                                    Are you sure you wish to create the {eventName} event?
                                </DialogContentText>

                        }
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={closeDialog} color="secondary">
                            No
                        </Button>
                        <Button onClick={dialogState === PatientSummaryDialogState.CreateAndNotify ? onCreateAndNotifyClick : onCreateClick} color="primary" variant="contained" autoFocus>
                            Yes
                        </Button>
                    </DialogActions>
                </Dialog>
            </>;
        }

        return (

            <StyledTooltip
                title="Complete the previous event to continue"
            >
                <StyledTooltipWrapper>
                    {createButton}
                    {createAndNotifyButton}
                </StyledTooltipWrapper>
            </StyledTooltip>
        );
    }, [onCreateClick, onResendClick, saving, sendingEmail, patientValid, updateTo, hideButton, closeDialog, dialogState, eventName, eventTitle, openCreateAndNotifyDialog, openCreateDialog, openResendDialog, onCreateAndNotifyClick]);

    return action;
};

export default usePatientSummaryCreateAndNotifyActions;